import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Api,
  Bolt,
  Info,
  Lock,
  Psychology,
  RocketLaunch,
  ShoppingBag,
  Tune,
  VerifiedUser
} from '@mui/icons-material';

import { useLanguage } from '../contexts/LanguageContext';
import {
  defaultCapabilityProfile,
  loadCapabilityProfile,
  resolveApiBaseUrl,
  resolveAuthToken,
  saveCapabilityProfile,
  type CapabilityContext,
  type CapabilityProfile,
  type CapabilityStrategyTier
} from '../lib/utils';

type SaveStatus = 'idle' | 'saving' | 'saved';

type StrategyOption = {
  value: CapabilityStrategyTier;
  label: string;
  desc: string;
};

export const CapabilitySettingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<CapabilityProfile>({ ...defaultCapabilityProfile });
  const [context, setContext] = useState<CapabilityContext | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isLoading, setIsLoading] = useState(true);
  const token = useMemo(() => resolveAuthToken(), []);
  const isAuthenticated = Boolean(token);
  const isTrialUser = !isAuthenticated;

  const strategyOptions: StrategyOption[] = [
    {
      value: 'economy',
      label: t('capability.strategyEconomy'),
      desc: t('capability.strategyEconomyDesc')
    },
    {
      value: 'balanced',
      label: t('capability.strategyBalanced'),
      desc: t('capability.strategyBalancedDesc')
    },
    {
      value: 'premium',
      label: t('capability.strategyPremium'),
      desc: t('capability.strategyPremiumDesc')
    }
  ];

  const updateProfile = (next: Partial<CapabilityProfile>) => {
    setProfile((prev) => ({
      ...prev,
      ...next
    }));
  };

  const buildLocalContext = (current: CapabilityProfile): CapabilityContext => {
    const presets: Record<CapabilityStrategyTier, { maxImages: number; theme: string; textModel: string }> = {
      economy: { maxImages: 3, theme: 'minimal', textModel: 'gemini-3-flash-preview' },
      balanced: { maxImages: 6, theme: 'modern', textModel: 'gemini-3-flash-preview' },
      premium: { maxImages: 8, theme: 'premium', textModel: 'gemini-3-pro-preview' }
    };
    const preset = presets[current.strategy_tier] ?? presets.balanced;
    const summary = [
      t('capability.summaryImages', { count: preset.maxImages }),
      t('capability.summarySeo', { level: current.seo_level }),
      t('capability.summaryRich', { theme: preset.theme }),
      current.brand_guard ? t('capability.summaryBrandOn') : t('capability.summaryBrandOff')
    ];
    if (current.platform === 'amazon') {
      summary.push(t('capability.summaryDegradedAmazon'));
    }
    return {
      ...current,
      image_generation: {
        enabled: true,
        max_images: preset.maxImages,
        style_preset: 'studio'
      },
      rich_content: {
        enabled: current.platform !== 'amazon',
        theme: preset.theme
      },
      model_preset: {
        text: preset.textModel,
        image: 'gemini-3-pro-image-preview'
      },
      summary
    };
  };

  const fetchProfile = async () => {
    if (!isAuthenticated || !token) {
      setProfile(loadCapabilityProfile());
      setIsLoading(false);
      return;
    }

    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/capabilities/profile`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.profile) {
        setProfile(payload.profile as CapabilityProfile);
      } else {
        setProfile(loadCapabilityProfile());
      }
    } catch (error) {
      setProfile(loadCapabilityProfile());
    } finally {
      setIsLoading(false);
    }
  };

  const resolveContext = async (current: CapabilityProfile) => {
    if (!isAuthenticated || !token) {
      setContext(buildLocalContext(current));
      return;
    }

    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/capabilities/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          platform: current.platform,
          override: {
            strategy_tier: current.strategy_tier,
            brand_guard: current.brand_guard
          }
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.context) {
        setContext(payload.context as CapabilityContext);
        return;
      }
    } catch (error) {
      // ignore and fallback to local summary
    }

    setContext(buildLocalContext(current));
  };

  const persistProfile = async (next: CapabilityProfile) => {
    setSaveStatus('saving');
    if (!isAuthenticated || !token) {
      saveCapabilityProfile(next);
      window.setTimeout(() => {
        setSaveStatus('saved');
        window.setTimeout(() => setSaveStatus('idle'), 1600);
      }, 400);
      return;
    }

    try {
      const apiBaseUrl = resolveApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/v1/capabilities/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(next)
      });
      if (!response.ok) {
        throw new Error('保存失败');
      }
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus('idle'), 1600);
    } catch (error) {
      saveCapabilityProfile(next);
      setSaveStatus('idle');
    }
  };

  const handleUseDefault = () => {
    const nextProfile = { ...defaultCapabilityProfile };
    setProfile(nextProfile);
    persistProfile(nextProfile);
  };

  const handleSaveDefault = () => {
    persistProfile(profile);
  };

  const handleLaunch = () => {
    if (!isAuthenticated) {
      saveCapabilityProfile(profile);
    }
    navigate('/workspace');
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      resolveContext(profile);
    }
  }, [profile, isLoading]);

  return (
    <div className="min-h-screen bg-[#FAFBFC] text-slate-900 flex items-center justify-center py-20 px-4 font-sans">
      <div className="max-w-6xl w-full animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-600 rounded-2xl mb-6 shadow-lg shadow-blue-200">
            <Tune className="text-white text-2xl" fontSize="large" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-[#0F172A]">{t('capability.title')}</h1>
          <p className="text-[#94A3B8] mt-3 text-lg font-light max-w-xl mx-auto">{t('capability.subtitle')}</p>
          {isTrialUser && (
            <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-2 text-[11px] font-semibold text-amber-600">
              <span className="material-symbols-outlined text-[14px]">lock</span>
              <span>{t('aiConfig.trialImageBadge')}</span>
              <button className="text-amber-700 font-bold hover:text-amber-800 underline underline-offset-4">
                {t('aiConfig.trialUpgradeCta')}
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)]">
          <div className="p-10 md:p-14 space-y-10">
            <section>
              <div className="flex justify-between items-end mb-6">
                <label className="text-xs font-semibold tracking-widest text-[#94A3B8] uppercase">{t('capability.targetPlatform')}</label>
                <span className="text-[10px] text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded">{t('capability.platformActive')}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative border-[3px] border-primary rounded-2xl p-6 transition-all duration-300 flex flex-col items-center justify-center gap-3 bg-blue-50/50 shadow-sm">
                  <span className="absolute -top-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm bg-emerald-100 text-emerald-700 border border-emerald-200">{t('capability.supported')}</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <Bolt className="text-4xl text-blue-600" style={{ fontSize: 36 }} />
                  </div>
                  <span className="text-sm font-bold text-[#0F172A]">Ozon</span>
                  <p className="text-[11px] text-slate-500 text-center">{t('capability.platformOzonDesc')}</p>
                </div>

                <div className="relative border border-slate-200 rounded-2xl p-6 transition-all duration-300 flex flex-col items-center justify-center gap-3 bg-white opacity-70">
                  <span className="absolute -top-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm bg-slate-100 text-slate-500 border border-slate-200">{t('capability.comingSoon')}</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <ShoppingBag className="text-4xl text-orange-400" style={{ fontSize: 36 }} />
                  </div>
                  <span className="text-sm font-bold text-[#0F172A]">Amazon</span>
                  <p className="text-[11px] text-slate-400 text-center">{t('capability.platformAmazonDesc')}</p>
                </div>
              </div>
            </section>

            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 flex gap-4">
              <Info className="text-blue-500" />
              <p className="text-xs leading-relaxed text-slate-600">
                <strong className="text-slate-900">{t('capability.systemNotice')}:</strong> {t('capability.systemNoticeBody')}
              </p>
            </div>

            <section>
              <label className="text-xs font-semibold tracking-widest text-[#94A3B8] uppercase mb-4 block">{t('capability.strategyTier')}</label>
              <div className="grid md:grid-cols-3 gap-4">
                {strategyOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateProfile({ strategy_tier: option.value })}
                    className={`text-left border rounded-2xl p-5 transition-all ${
                      profile.strategy_tier === option.value
                        ? 'border-primary bg-blue-50/40 shadow-sm'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-sm font-bold text-slate-900">{option.label}</p>
                    <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">{option.desc}</p>
                  </button>
                ))}
              </div>
            </section>

            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                    <VerifiedUser className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">{t('capability.brandGuardTitle')}</p>
                    <p className="text-xs text-[#94A3B8]">{t('capability.brandGuardDesc')}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    checked={profile.brand_guard}
                    onChange={(event) => updateProfile({ brand_guard: event.target.checked })}
                    className="sr-only peer"
                    type="checkbox"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <Lock className="text-slate-400" fontSize="small" />
                <span>{t('capability.brandGuardHint')}</span>
              </div>
            </section>

            <section className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-900">{t('capability.summaryTitle')}</h3>
                <span className="text-[10px] font-semibold text-slate-400">{t('capability.summaryHint')}</span>
              </div>
              <ul className="space-y-2 text-xs text-slate-600">
                {(context?.summary || []).map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={handleSaveDefault}
                className="px-4 py-3 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all"
              >
                {saveStatus === 'saving' ? t('capability.saving') : saveStatus === 'saved' ? t('capability.saved') : t('capability.saveDefault')}
              </button>
              <button
                onClick={handleUseDefault}
                className="px-4 py-3 rounded-2xl text-slate-500 text-sm font-semibold hover:text-slate-700 transition-all"
              >
                {t('capability.useDefault')}
              </button>
              <button
                onClick={handleLaunch}
                className="bg-primary hover:bg-blue-700 text-white font-bold py-3 rounded-2xl text-sm flex items-center justify-center gap-3 shadow-lg shadow-blue-100 transition-all"
              >
                <RocketLaunch style={{ fontSize: 22 }} />
                {t('capability.launchBtn')}
              </button>
            </div>
          </div>
        </div>

        <footer className="mt-12 border-t border-slate-200 pt-8">
          <div className="flex justify-center items-center gap-10 text-slate-400">
            <div className="flex items-center gap-2.5">
              <VerifiedUser fontSize="small" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t('capability.secure')}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Psychology fontSize="small" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t('capability.neural')}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Api fontSize="small" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t('capability.apiSync')}</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
